/**
 * confirm — a modal that has to be answered before anything is destroyed.
 *
 * Built on the native <dialog> element rather than a div: it gets focus
 * trapping, Escape to dismiss, inert background content and the right ARIA
 * semantics from the browser, none of which are worth reimplementing badly.
 *
 * Replaces window.confirm, which cannot be styled, cannot carry a second line
 * of explanation, and is suppressible in a way that silently turns a
 * destructive action into an unconfirmed one.
 */

let dialog = null;

/**
 * @param {object} options
 * @param {string} options.title
 * @param {string} [options.body] the consequence, in plain terms
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @param {boolean} [options.destructive] style the confirm button as a loss
 * @returns {Promise<boolean>}
 */
export function confirmAction(options) {
  const {
    title,
    body = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = true,
  } = options;

  // Without a working <dialog> the action still has to be answerable, so fall
  // back to the browser's own prompt rather than letting it through
  // unconfirmed. Testing showModal rather than the constructor: some engines
  // define the type without implementing the method.
  if (typeof HTMLDialogElement === 'undefined'
      || typeof HTMLDialogElement.prototype.showModal !== 'function') {
    return Promise.resolve(window.confirm(`${title}\n\n${body}`));
  }

  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.className = 'confirm';
    document.body.appendChild(dialog);
  }

  dialog.innerHTML = `
    <form method="dialog">
      <h2>${escapeHtml(title)}</h2>
      ${body ? `<p>${escapeHtml(body)}</p>` : ''}
      <div class="confirm-actions">
        <button type="submit" value="cancel" data-cancel>${escapeHtml(cancelLabel)}</button>
        <button type="submit" value="confirm" data-confirm
                class="${destructive ? 'destructive' : 'primary'}">
          ${escapeHtml(confirmLabel)}
        </button>
      </div>
    </form>`;

  return new Promise((resolve) => {
    const done = () => {
      dialog.removeEventListener('close', done);
      // Escape closes with an empty value, which is a cancellation.
      resolve(dialog.returnValue === 'confirm');
    };

    dialog.addEventListener('close', done);
    dialog.returnValue = '';

    // Close explicitly on click rather than relying on the form's implicit
    // submission. method="dialog" is the tidier mechanism but it is the part
    // of <dialog> engines are least consistent about, and a button that does
    // nothing is a worse failure than a slightly redundant handler.
    for (const button of dialog.querySelectorAll('button[value]')) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        dialog.close(button.value);
      });
    }

    dialog.showModal();

    // Cancel takes focus, not confirm. A dialog that destroys something on a
    // stray Enter is worse than no dialog: the keypress that opened it can
    // carry straight through to the default button.
    const cancel = dialog.querySelector('[data-cancel]');
    if (cancel) cancel.focus();
  });
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
